/**
 * Book-level location roster + relationship graph (desktop-v2).
 * Source of truth: `book.metadata.locations` + `book.metadata.locationRelationships`.
 * Chapter `tabData.locations` is a derived name list.
 */

export type LocationType =
  | "room"
  | "building"
  | "settlement"
  | "region"
  | "landmark"
  | "other";

export type LocationRelationshipKind =
  | "adjacent"
  | "connection"
  | "route"
  | "proximity"
  | "custom";

export type DistanceUnit = "m" | "km" | "ft" | "mi" | "blocks" | "minutes" | "hours" | "other";

export type LocationChapterAppearance = {
  chapterId: string;
  note?: string;
};

export type BookLocation = {
  id: string;
  name: string;
  aliases: string[];
  type: LocationType;
  description: string;
  /** Containment hierarchy: room → building → settlement → region. */
  parentLocationId?: string;
  firstAppearanceChapterId?: string;
  appearances: LocationChapterAppearance[];
  updatedAt: string;
};

export type LocationRelationship = {
  id: string;
  fromLocationId: string;
  toLocationId: string;
  kind: LocationRelationshipKind;
  distance?: number;
  distanceUnit?: DistanceUnit;
  travelTime?: string;
  travelMode?: string;
  direction?: string;
  description: string;
  updatedAt: string;
};

/** Author-removed location with reason — informs future extract / roster versions. */
export type LocationRemovalRecord = {
  id: string;
  locationId?: string;
  name: string;
  aliases: string[];
  description?: string;
  type?: LocationType;
  reason: string;
  removedAt: string;
};

export const LOCATION_TYPES: readonly LocationType[] = [
  "region",
  "settlement",
  "building",
  "room",
  "landmark",
  "other",
] as const;

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  region: "Region",
  settlement: "Settlement",
  building: "Building",
  room: "Room",
  landmark: "Landmark",
  other: "Other",
};

export const LOCATION_RELATIONSHIP_KINDS: readonly LocationRelationshipKind[] = [
  "adjacent",
  "connection",
  "route",
  "proximity",
  "custom",
] as const;

export const LOCATION_RELATIONSHIP_LABELS: Record<LocationRelationshipKind, string> = {
  adjacent: "Adjacent",
  connection: "Connection",
  route: "Route",
  proximity: "Proximity",
  custom: "Custom",
};

export const DISTANCE_UNITS: readonly DistanceUnit[] = [
  "m",
  "km",
  "ft",
  "mi",
  "blocks",
  "minutes",
  "hours",
  "other",
] as const;

const TYPE_SORT_ORDER: Record<LocationType, number> = {
  region: 0,
  settlement: 1,
  building: 2,
  room: 3,
  landmark: 4,
  other: 5,
};

export function generateLocationId(): string {
  return `loc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateLocationRelationshipId(): string {
  return `loc-rel-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateLocationRemovalId(): string {
  return `loc-removal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeNameKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Reject common false positives from capitalized-word heuristics. */
const LOCATION_BLOCKLIST = new Set([
  "the",
  "a",
  "an",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
  "halloween",
  "christmas",
  "thanksgiving",
  "easter",
  "today",
  "tomorrow",
  "yesterday",
  "morning",
  "evening",
  "afternoon",
  "night",
  "class",
  "school",
  "home",
  "work",
]);

export function isPlausibleLocationName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length < 2) return false;
  const key = normalizeNameKey(trimmed);
  if (!key) return false;
  if (LOCATION_BLOCKLIST.has(key)) return false;
  if (
    /^(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+(second|seconds|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)$/.test(
      key
    )
  ) {
    return false;
  }
  if (/^(before|after|during|until|since|by|on|at|in|within|throughout)\s+/.test(key)) {
    return false;
  }
  // Require at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  return true;
}

export function filterPlausibleExtractedLocations<T extends { name?: string }>(items: T[]): T[] {
  return items.filter((item) => {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    return name && isPlausibleLocationName(name);
  });
}

export function normalizeLocationType(value: unknown): LocationType {
  if (typeof value !== "string") return "other";
  const key = value.trim().toLowerCase().replace(/\s+/g, "_");
  if (key === "room" || key === "chamber" || key === "office" || key === "hallway") return "room";
  if (key === "building" || key === "house" || key === "structure" || key === "facility") {
    return "building";
  }
  if (
    key === "settlement" ||
    key === "city" ||
    key === "town" ||
    key === "village" ||
    key === "neighborhood" ||
    key === "district"
  ) {
    return "settlement";
  }
  if (key === "region" || key === "country" || key === "continent" || key === "area" || key === "world") {
    return "region";
  }
  if (key === "landmark" || key === "place" || key === "site") return "landmark";
  if ((LOCATION_TYPES as readonly string[]).includes(key)) return key as LocationType;
  return "other";
}

export function normalizeRelationshipKind(value: unknown): LocationRelationshipKind {
  if (typeof value !== "string") return "custom";
  const key = value.trim().toLowerCase().replace(/\s+/g, "_");
  if (key === "adjacent" || key === "next_to" || key === "beside") return "adjacent";
  if (key === "connection" || key === "connected" || key === "connects" || key === "leads_to") {
    return "connection";
  }
  if (key === "route" || key === "path" || key === "road" || key === "travel") return "route";
  if (key === "proximity" || key === "near" || key === "nearby") return "proximity";
  if ((LOCATION_RELATIONSHIP_KINDS as readonly string[]).includes(key)) {
    return key as LocationRelationshipKind;
  }
  return "custom";
}

export function normalizeDistanceUnit(value: unknown): DistanceUnit | undefined {
  if (typeof value !== "string") return undefined;
  const key = value.trim().toLowerCase();
  if (key === "meters" || key === "meter" || key === "metres" || key === "metre") return "m";
  if (key === "kilometers" || key === "kilometer" || key === "kilometres" || key === "kilometre") {
    return "km";
  }
  if (key === "feet" || key === "foot") return "ft";
  if (key === "miles" || key === "mile") return "mi";
  if (key === "block") return "blocks";
  if (key === "minute" || key === "min") return "minutes";
  if (key === "hour" || key === "hr") return "hours";
  if ((DISTANCE_UNITS as readonly string[]).includes(key)) return key as DistanceUnit;
  return "other";
}

export function normalizeAppearance(raw: unknown): LocationChapterAppearance | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const chapterId = typeof rec.chapterId === "string" ? rec.chapterId.trim() : "";
  if (!chapterId) return null;
  const note = typeof rec.note === "string" ? rec.note.trim() : undefined;
  return {
    chapterId,
    ...(note ? { note } : {}),
  };
}

export function normalizeBookLocation(raw: unknown, nowIso = new Date().toISOString()): BookLocation | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  if (!name) return null;
  const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateLocationId();
  const aliases = Array.isArray(rec.aliases)
    ? rec.aliases
        .filter((a): a is string => typeof a === "string")
        .map((a) => a.trim())
        .filter(Boolean)
    : [];
  const description = typeof rec.description === "string" ? rec.description.trim() : "";
  const type = normalizeLocationType(rec.type);
  const parentLocationId =
    typeof rec.parentLocationId === "string" && rec.parentLocationId.trim()
      ? rec.parentLocationId.trim()
      : undefined;
  const firstAppearanceChapterId =
    typeof rec.firstAppearanceChapterId === "string" && rec.firstAppearanceChapterId.trim()
      ? rec.firstAppearanceChapterId.trim()
      : typeof rec.firstAppearance === "string" && rec.firstAppearance.trim()
        ? rec.firstAppearance.trim()
        : undefined;
  const appearances: LocationChapterAppearance[] = [];
  if (Array.isArray(rec.appearances)) {
    for (const item of rec.appearances) {
      const appearance = normalizeAppearance(item);
      if (appearance) appearances.push(appearance);
    }
  } else if (Array.isArray(rec.mentions)) {
    for (const item of rec.mentions) {
      if (!item || typeof item !== "object") continue;
      const m = item as Record<string, unknown>;
      const chapterId = typeof m.chapterId === "string" ? m.chapterId.trim() : "";
      if (chapterId) appearances.push({ chapterId });
    }
  }
  const updatedAt =
    typeof rec.updatedAt === "string" && rec.updatedAt.trim() ? rec.updatedAt.trim() : nowIso;

  const location: BookLocation = {
    id,
    name,
    aliases,
    type,
    description,
    appearances,
    updatedAt,
  };
  if (parentLocationId && parentLocationId !== id) location.parentLocationId = parentLocationId;
  if (firstAppearanceChapterId) location.firstAppearanceChapterId = firstAppearanceChapterId;
  return location;
}

/** Remove parent cycles (A→B→A) by clearing offending parent links. */
export function sanitizeLocationHierarchy(roster: BookLocation[]): BookLocation[] {
  const byId = new Map(roster.map((loc) => [loc.id, loc]));
  return roster.map((loc) => {
    if (!loc.parentLocationId) return loc;
    if (!byId.has(loc.parentLocationId)) {
      const { parentLocationId: _drop, ...rest } = loc;
      return rest;
    }
    const seen = new Set<string>([loc.id]);
    let current: string | undefined = loc.parentLocationId;
    while (current) {
      if (seen.has(current)) {
        const { parentLocationId: _drop, ...rest } = loc;
        return rest;
      }
      seen.add(current);
      current = byId.get(current)?.parentLocationId;
    }
    return loc;
  });
}

export function sortLocations(roster: BookLocation[]): BookLocation[] {
  return [...roster].sort((a, b) => {
    const typeDiff = TYPE_SORT_ORDER[a.type] - TYPE_SORT_ORDER[b.type];
    if (typeDiff !== 0) return typeDiff;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

export function normalizeLocationRoster(raw: unknown): BookLocation[] {
  if (!Array.isArray(raw)) return [];
  const out: BookLocation[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const location = normalizeBookLocation(item);
    if (!location) continue;
    if (!isPlausibleLocationName(location.name)) continue;
    if (seenIds.has(location.id)) {
      location.id = generateLocationId();
    }
    seenIds.add(location.id);
    out.push(location);
  }
  return sortLocations(sanitizeLocationHierarchy(out));
}

export function locationNameKeys(location: BookLocation): string[] {
  const keys = new Set<string>();
  const name = normalizeNameKey(location.name);
  if (name) keys.add(name);
  for (const alias of location.aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  return Array.from(keys);
}

export function findLocationByNameOrAlias(
  roster: BookLocation[],
  name: string,
  aliases: string[] = []
): BookLocation | undefined {
  const keys = new Set<string>();
  const primary = normalizeNameKey(name);
  if (primary) keys.add(primary);
  for (const alias of aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  if (keys.size === 0) return undefined;

  const exact = roster.find((location) => locationNameKeys(location).some((k) => keys.has(k)));
  if (exact) return exact;

  return roster.find((location) => {
    const rosterTokens = new Set(
      locationNameKeys(location).flatMap((k) => k.split(" ").filter((t) => t.length > 1))
    );
    for (const key of keys) {
      const tokens = key.split(" ").filter((t) => t.length > 1);
      if (tokens.length === 1 && rosterTokens.has(tokens[0]!)) return true;
      if (tokens.length > 1 && tokens.every((t) => rosterTokens.has(t))) return true;
    }
    return false;
  });
}

export type ExtractedLocationPayload = {
  name: string;
  aliases?: string[];
  description?: string;
  type?: LocationType | string;
  note?: string;
  /** Parent location name (resolved to id on merge). */
  parentName?: string;
  /** Whole-book extraction: chapters where this location appears. */
  chapterIds?: string[];
};

export type ExtractedLocationRelationshipPayload = {
  fromName: string;
  toName: string;
  kind?: LocationRelationshipKind | string;
  distance?: number;
  distanceUnit?: DistanceUnit | string;
  travelTime?: string;
  travelMode?: string;
  direction?: string;
  description?: string;
};

export function mergeExtractedLocationsIntoRoster(params: {
  roster: BookLocation[];
  extracted: ExtractedLocationPayload[];
  chapterId: string;
  removals?: LocationRemovalRecord[];
  nowIso?: string;
}): BookLocation[] {
  const now = params.nowIso ?? new Date().toISOString();
  const removals = params.removals ?? [];
  let next = [...params.roster];

  // First pass: upsert locations without parents
  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleLocationName(name)) continue;
    const aliases = Array.isArray(item.aliases)
      ? item.aliases
          .filter((a): a is string => typeof a === "string")
          .map((a) => a.trim())
          .filter(Boolean)
      : [];
    if (isLocationNameRemoved(removals, name, aliases)) continue;
    const type = normalizeLocationType(item.type);
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const note = typeof item.note === "string" ? item.note.trim() : undefined;

    const existing = findLocationByNameOrAlias(next, name, aliases);
    if (existing) {
      const mergedAliases = Array.from(
        new Set([
          ...existing.aliases,
          ...aliases.filter((a) => normalizeNameKey(a) !== normalizeNameKey(existing.name)),
        ])
      );
      const preferName = name.length > existing.name.length ? name : existing.name;
      const appearance: LocationChapterAppearance = {
        chapterId: params.chapterId,
        ...(note ? { note } : {}),
      };
      const otherAppearances = existing.appearances.filter((a) => a.chapterId !== params.chapterId);
      const prevAppearance = existing.appearances.find((a) => a.chapterId === params.chapterId);
      if (prevAppearance?.note && !appearance.note) appearance.note = prevAppearance.note;

      next = next.map((loc) =>
        loc.id === existing.id
          ? {
              ...loc,
              name: preferName,
              aliases: mergedAliases,
              type: type !== "other" || !loc.type ? type : loc.type,
              description: loc.description.trim() || description,
              firstAppearanceChapterId: loc.firstAppearanceChapterId ?? params.chapterId,
              appearances: [...otherAppearances, appearance],
              updatedAt: now,
            }
          : loc
      );
    } else {
      next.push({
        id: generateLocationId(),
        name,
        aliases,
        type,
        description,
        firstAppearanceChapterId: params.chapterId,
        appearances: [
          {
            chapterId: params.chapterId,
            ...(note ? { note } : {}),
          },
        ],
        updatedAt: now,
      });
    }
  }

  // Second pass: apply parent links by name
  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const parentName = typeof item.parentName === "string" ? item.parentName.trim() : "";
    if (!name || !parentName) continue;
    const child = findLocationByNameOrAlias(next, name, item.aliases ?? []);
    const parent = findLocationByNameOrAlias(next, parentName);
    if (!child || !parent || child.id === parent.id) continue;
    next = next.map((loc) =>
      loc.id === child.id ? { ...loc, parentLocationId: parent.id, updatedAt: now } : loc
    );
  }

  return sortLocations(sanitizeLocationHierarchy(next));
}

/**
 * Merge a whole-book location pass. Holistic descriptions and hierarchy are authoritative.
 * Existing chapter notes are preserved.
 */
export function mergeHolisticLocationsIntoRoster(params: {
  roster: BookLocation[];
  extracted: ExtractedLocationPayload[];
  validChapterIds: string[];
  removals?: LocationRemovalRecord[];
  nowIso?: string;
}): BookLocation[] {
  const now = params.nowIso ?? new Date().toISOString();
  const validIds = new Set(params.validChapterIds);
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const aliases = Array.isArray(item.aliases)
      ? item.aliases
          .filter((alias): alias is string => typeof alias === "string")
          .map((alias) => alias.trim())
          .filter(Boolean)
      : [];
    if (!name || !isPlausibleLocationName(name) || isLocationNameRemoved(removals, name, aliases)) {
      continue;
    }

    const type = normalizeLocationType(item.type);
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const chapterIds = Array.from(
      new Set(
        (Array.isArray(item.chapterIds) ? item.chapterIds : [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.trim())
          .filter((id) => validIds.has(id))
      )
    );
    const existing = findLocationByNameOrAlias(next, name, aliases);

    if (existing) {
      const existingByChapter = new Map(
        existing.appearances.map((appearance) => [appearance.chapterId, appearance])
      );
      const mergedChapterIds = Array.from(
        new Set([...existing.appearances.map((appearance) => appearance.chapterId), ...chapterIds])
      ).filter((id) => validIds.has(id));
      const appearances = mergedChapterIds.map(
        (chapterId) => existingByChapter.get(chapterId) ?? { chapterId }
      );
      const mergedAliases = Array.from(
        new Set([
          ...existing.aliases,
          ...aliases.filter((alias) => normalizeNameKey(alias) !== normalizeNameKey(existing.name)),
        ])
      );
      const preferName = name.length > existing.name.length ? name : existing.name;
      next = next.map((location) =>
        location.id === existing.id
          ? {
              ...location,
              name: preferName,
              aliases: mergedAliases,
              type: type !== "other" || !location.type ? type : location.type,
              description: description || location.description,
              firstAppearanceChapterId: location.firstAppearanceChapterId ?? chapterIds[0],
              appearances,
              updatedAt: now,
            }
          : location
      );
      continue;
    }

    next.push({
      id: generateLocationId(),
      name,
      aliases,
      type,
      description,
      firstAppearanceChapterId: chapterIds[0],
      appearances: chapterIds.map((chapterId) => ({ chapterId })),
      updatedAt: now,
    });
  }

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const parentName = typeof item.parentName === "string" ? item.parentName.trim() : "";
    if (!name || !parentName) continue;
    const child = findLocationByNameOrAlias(next, name, item.aliases ?? []);
    const parent = findLocationByNameOrAlias(next, parentName);
    if (!child || !parent || child.id === parent.id) continue;
    next = next.map((loc) =>
      loc.id === child.id ? { ...loc, parentLocationId: parent.id, updatedAt: now } : loc
    );
  }

  return sortLocations(sanitizeLocationHierarchy(next));
}

export function normalizeLocationRelationship(
  raw: unknown,
  nowIso = new Date().toISOString()
): LocationRelationship | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const fromLocationId =
    typeof rec.fromLocationId === "string" ? rec.fromLocationId.trim() : "";
  const toLocationId = typeof rec.toLocationId === "string" ? rec.toLocationId.trim() : "";
  if (!fromLocationId || !toLocationId || fromLocationId === toLocationId) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateLocationRelationshipId();
  const kind = normalizeRelationshipKind(rec.kind);
  const description = typeof rec.description === "string" ? rec.description.trim() : "";
  const distance =
    typeof rec.distance === "number" && Number.isFinite(rec.distance)
      ? rec.distance
      : typeof rec.distance === "string" && rec.distance.trim() && Number.isFinite(Number(rec.distance))
        ? Number(rec.distance)
        : undefined;
  const distanceUnit = normalizeDistanceUnit(rec.distanceUnit);
  const travelTime = typeof rec.travelTime === "string" ? rec.travelTime.trim() : undefined;
  const travelMode = typeof rec.travelMode === "string" ? rec.travelMode.trim() : undefined;
  const direction = typeof rec.direction === "string" ? rec.direction.trim() : undefined;
  const updatedAt =
    typeof rec.updatedAt === "string" && rec.updatedAt.trim() ? rec.updatedAt.trim() : nowIso;

  return {
    id,
    fromLocationId,
    toLocationId,
    kind,
    ...(distance != null ? { distance } : {}),
    ...(distanceUnit ? { distanceUnit } : {}),
    ...(travelTime ? { travelTime } : {}),
    ...(travelMode ? { travelMode } : {}),
    ...(direction ? { direction } : {}),
    description,
    updatedAt,
  };
}

function relationshipPairKey(fromId: string, toId: string, kind: LocationRelationshipKind): string {
  const [a, b] = [fromId, toId].sort();
  return `${a}::${b}::${kind}`;
}

export function normalizeLocationRelationships(
  raw: unknown,
  validLocationIds?: Set<string>
): LocationRelationship[] {
  if (!Array.isArray(raw)) return [];
  const out: LocationRelationship[] = [];
  const seenPairs = new Set<string>();
  const seenIds = new Set<string>();
  for (const item of raw) {
    const rel = normalizeLocationRelationship(item);
    if (!rel) continue;
    if (validLocationIds) {
      if (!validLocationIds.has(rel.fromLocationId) || !validLocationIds.has(rel.toLocationId)) {
        continue;
      }
    }
    if (seenIds.has(rel.id)) {
      rel.id = generateLocationRelationshipId();
    }
    const pairKey = relationshipPairKey(rel.fromLocationId, rel.toLocationId, rel.kind);
    if (seenPairs.has(pairKey)) continue;
    seenPairs.add(pairKey);
    seenIds.add(rel.id);
    out.push(rel);
  }
  return out.sort((a, b) => a.kind.localeCompare(b.kind) || a.id.localeCompare(b.id));
}

export function mergeExtractedRelationships(params: {
  relationships: LocationRelationship[];
  extracted: ExtractedLocationRelationshipPayload[];
  roster: BookLocation[];
  nowIso?: string;
}): LocationRelationship[] {
  const now = params.nowIso ?? new Date().toISOString();
  const next = [...params.relationships];
  const validIds = new Set(params.roster.map((loc) => loc.id));

  for (const item of params.extracted) {
    const fromName = typeof item.fromName === "string" ? item.fromName.trim() : "";
    const toName = typeof item.toName === "string" ? item.toName.trim() : "";
    if (!fromName || !toName) continue;
    const from = findLocationByNameOrAlias(params.roster, fromName);
    const to = findLocationByNameOrAlias(params.roster, toName);
    if (!from || !to || from.id === to.id) continue;

    const kind = normalizeRelationshipKind(item.kind);
    const pairKey = relationshipPairKey(from.id, to.id, kind);
    const existingIndex = next.findIndex(
      (rel) => relationshipPairKey(rel.fromLocationId, rel.toLocationId, rel.kind) === pairKey
    );

    const distance =
      typeof item.distance === "number" && Number.isFinite(item.distance) ? item.distance : undefined;
    const distanceUnit = normalizeDistanceUnit(item.distanceUnit);
    const travelTime = typeof item.travelTime === "string" ? item.travelTime.trim() : undefined;
    const travelMode = typeof item.travelMode === "string" ? item.travelMode.trim() : undefined;
    const direction = typeof item.direction === "string" ? item.direction.trim() : undefined;
    const description = typeof item.description === "string" ? item.description.trim() : "";

    if (existingIndex >= 0) {
      const existing = next[existingIndex]!;
      next[existingIndex] = {
        ...existing,
        ...(distance != null ? { distance } : {}),
        ...(distanceUnit ? { distanceUnit } : {}),
        ...(travelTime ? { travelTime } : {}),
        ...(travelMode ? { travelMode } : {}),
        ...(direction ? { direction } : {}),
        description: description || existing.description,
        updatedAt: now,
      };
    } else {
      next.push({
        id: generateLocationRelationshipId(),
        fromLocationId: from.id,
        toLocationId: to.id,
        kind,
        ...(distance != null ? { distance } : {}),
        ...(distanceUnit ? { distanceUnit } : {}),
        ...(travelTime ? { travelTime } : {}),
        ...(travelMode ? { travelMode } : {}),
        ...(direction ? { direction } : {}),
        description,
        updatedAt: now,
      });
    }
  }

  return normalizeLocationRelationships(next, validIds);
}

export function chapterLocationTabText(roster: BookLocation[], chapterId: string): string {
  const inChapter = roster.filter((loc) => loc.appearances.some((a) => a.chapterId === chapterId));
  return sortLocations(inChapter)
    .map((loc) => loc.name)
    .join("\n");
}

export function syncAllChapterLocationTabs(
  chapters: Array<{ id: string; tabData: { locations: string } }>,
  roster: BookLocation[]
): void {
  for (const chapter of chapters) {
    chapter.tabData.locations = chapterLocationTabText(roster, chapter.id);
  }
}

export function getLocationsFromBookMetadata(
  metadata: Record<string, unknown> | undefined | null
): BookLocation[] {
  if (!metadata) return [];
  return normalizeLocationRoster(metadata.locations);
}

export function getLocationRelationshipsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): LocationRelationship[] {
  if (!metadata) return [];
  const roster = getLocationsFromBookMetadata(metadata);
  const validIds = new Set(roster.map((loc) => loc.id));
  return normalizeLocationRelationships(metadata.locationRelationships, validIds);
}

export function normalizeLocationRemoval(raw: unknown): LocationRemovalRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  const reason = typeof rec.reason === "string" ? rec.reason.trim() : "";
  if (!name || !reason) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateLocationRemovalId();
  const aliases = Array.isArray(rec.aliases)
    ? rec.aliases
        .filter((a): a is string => typeof a === "string")
        .map((a) => a.trim())
        .filter(Boolean)
    : [];
  return {
    id,
    locationId: typeof rec.locationId === "string" ? rec.locationId.trim() : undefined,
    name,
    aliases,
    description: typeof rec.description === "string" ? rec.description.trim() : undefined,
    type: rec.type != null ? normalizeLocationType(rec.type) : undefined,
    reason,
    removedAt:
      typeof rec.removedAt === "string" && rec.removedAt.trim()
        ? rec.removedAt.trim()
        : new Date().toISOString(),
  };
}

export function normalizeLocationRemovals(raw: unknown): LocationRemovalRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: LocationRemovalRecord[] = [];
  for (const item of raw) {
    const record = normalizeLocationRemoval(item);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.removedAt.localeCompare(a.removedAt));
}

export function getLocationRemovalsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): LocationRemovalRecord[] {
  if (!metadata) return [];
  return normalizeLocationRemovals(metadata.locationRemovals);
}

export function locationRemovalNameKeys(record: LocationRemovalRecord): string[] {
  const keys = new Set<string>();
  const name = normalizeNameKey(record.name);
  if (name) keys.add(name);
  for (const alias of record.aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  return Array.from(keys);
}

export function isLocationNameRemoved(
  removals: LocationRemovalRecord[],
  name: string,
  aliases: string[] = []
): boolean {
  const keys = new Set<string>();
  const primary = normalizeNameKey(name);
  if (primary) keys.add(primary);
  for (const alias of aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  if (keys.size === 0) return false;
  return removals.some((record) =>
    locationRemovalNameKeys(record).some((removedKey) => keys.has(removedKey))
  );
}

export function createLocationRemovalRecord(
  location: Pick<BookLocation, "id" | "name" | "aliases" | "description" | "type">,
  reason: string,
  removedAt = new Date().toISOString()
): LocationRemovalRecord {
  return {
    id: generateLocationRemovalId(),
    locationId: location.id,
    name: location.name.trim(),
    aliases: [...location.aliases],
    description: location.description.trim() || undefined,
    type: location.type,
    reason: reason.trim(),
    removedAt,
  };
}

export function withLocationRemovalsInMetadata(
  metadata: Record<string, unknown>,
  removals: LocationRemovalRecord[]
): Record<string, unknown> {
  return {
    ...metadata,
    locationRemovals: normalizeLocationRemovals(removals),
  };
}

export function formatLocationRemovalsForExtractPrompt(removals: LocationRemovalRecord[]): string {
  if (!removals.length) return "";
  return removals
    .slice(0, 40)
    .map((record) => {
      const aliases = record.aliases.length ? ` (aka ${record.aliases.join(", ")})` : "";
      return `- "${record.name}"${aliases}: ${record.reason}`;
    })
    .join("\n");
}

export function withLocationsInMetadata(
  metadata: Record<string, unknown>,
  locations: BookLocation[],
  relationships?: LocationRelationship[]
): Record<string, unknown> {
  const roster = sortLocations(sanitizeLocationHierarchy(locations));
  const validIds = new Set(roster.map((loc) => loc.id));
  const next: Record<string, unknown> = {
    ...metadata,
    locations: roster,
  };
  if (relationships) {
    next.locationRelationships = normalizeLocationRelationships(relationships, validIds);
  } else if (Array.isArray(metadata.locationRelationships)) {
    next.locationRelationships = normalizeLocationRelationships(
      metadata.locationRelationships,
      validIds
    );
  }
  return next;
}

/** Seed location stubs from unique chapter tab name lines when roster is empty. */
export function seedLocationsFromChapterTabs(
  chapters: Array<{ id: string; order?: number; tabData?: { locations?: string } }>,
  removals: LocationRemovalRecord[] = []
): BookLocation[] {
  const now = new Date().toISOString();
  const byKey = new Map<string, BookLocation>();

  const ordered = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const chapter of ordered) {
    const lines = String(chapter.tabData?.locations ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    for (const name of lines) {
      if (!isPlausibleLocationName(name)) continue;
      if (isLocationNameRemoved(removals, name)) continue;
      const key = normalizeNameKey(name);
      if (!key) continue;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.appearances.some((a) => a.chapterId === chapter.id)) {
          existing.appearances.push({ chapterId: chapter.id });
        }
      } else {
        byKey.set(key, {
          id: generateLocationId(),
          name,
          aliases: [],
          type: "other",
          description: "",
          firstAppearanceChapterId: chapter.id,
          appearances: [{ chapterId: chapter.id }],
          updatedAt: now,
        });
      }
    }
  }
  return sortLocations(Array.from(byKey.values()));
}

export function appearanceForChapter(
  location: BookLocation,
  chapterId: string
): LocationChapterAppearance | undefined {
  return location.appearances.find((a) => a.chapterId === chapterId);
}

export function locationsInChapter(roster: BookLocation[], chapterId: string): BookLocation[] {
  return sortLocations(roster.filter((loc) => loc.appearances.some((a) => a.chapterId === chapterId)));
}

export function relationshipsForLocation(
  relationships: LocationRelationship[],
  locationId: string
): LocationRelationship[] {
  return relationships.filter(
    (rel) => rel.fromLocationId === locationId || rel.toLocationId === locationId
  );
}

export function childLocations(roster: BookLocation[], parentId: string): BookLocation[] {
  return sortLocations(roster.filter((loc) => loc.parentLocationId === parentId));
}

export function parentChain(roster: BookLocation[], locationId: string): BookLocation[] {
  const byId = new Map(roster.map((loc) => [loc.id, loc]));
  const chain: BookLocation[] = [];
  const seen = new Set<string>();
  let current = byId.get(locationId)?.parentLocationId;
  while (current && !seen.has(current)) {
    seen.add(current);
    const parent = byId.get(current);
    if (!parent) break;
    chain.push(parent);
    current = parent.parentLocationId;
  }
  return chain;
}

export function formatDistance(rel: LocationRelationship): string {
  if (rel.distance == null) return "";
  const unit = rel.distanceUnit ?? "";
  return `${rel.distance}${unit ? ` ${unit}` : ""}`.trim();
}

export function isStructuredLocationRecord(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const rec = item as Record<string, unknown>;
  return (
    typeof rec.name === "string" &&
    (Array.isArray(rec.appearances) ||
      Array.isArray(rec.aliases) ||
      typeof rec.description === "string" ||
      typeof rec.type === "string" ||
      typeof rec.parentLocationId === "string" ||
      typeof rec.id === "string")
  );
}

/** Prefer full roster JSON; fall back to empty (seed from chapter tabs instead). */
export function rosterFromExtractedLocationsJson(extracted: unknown): BookLocation[] {
  if (!Array.isArray(extracted) || extracted.length === 0) return [];
  const structured = extracted.every((item) => isStructuredLocationRecord(item));
  if (structured) {
    const roster = normalizeLocationRoster(extracted);
    if (
      roster.some(
        (loc) =>
          loc.appearances.length > 0 ||
          loc.description ||
          loc.aliases.length > 0 ||
          loc.type !== "other" ||
          loc.parentLocationId
      )
    ) {
      return roster;
    }
  }
  return [];
}
